import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Image as ImageIcon, FileText, Save, Loader2 } from 'lucide-react';

const SiteManager = () => {
  // Carousel state
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [url, setUrl] = useState('');
  
  // Content state
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadImages();
    loadContent();
  }, []);

  // ========== CAROUSEL FUNCTIONS ==========
  const loadImages = async () => {
    try {
      const res = await axios.get(`${API}/carousel-images`);
      setImages(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du carousel');
    } finally {
      setImagesLoading(false);
    }
  };

  const handleImageSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/carousel-images`, { url }, { withCredentials: true });
      toast.success('Image ajoutée');
      loadImages();
      setDialogOpen(false);
      setUrl('');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de l\'image');
    }
  };

  const handleImageDelete = async (id) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    try {
      await axios.delete(`${API}/carousel-images/${id}`, { withCredentials: true });
      toast.success('Image supprimée');
      loadImages();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ========== CONTENT FUNCTIONS ==========
  const loadContent = async () => {
    try {
      const res = await axios.get(`${API}/landing-content`, { withCredentials: true });
      setContent(res.data);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setContentLoading(false);
    }
  };

  const handleContentSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/landing-content`, content, { withCredentials: true });
      toast.success('Contenu mis à jour avec succès !');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateService = (index, field, value) => {
    const newServices = [...content.services];
    newServices[index][field] = value;
    setContent({ ...content, services: newServices });
  };

  const addService = () => {
    setContent({
      ...content,
      services: [...content.services, { title: '', description: '' }]
    });
  };

  const removeService = (index) => {
    const newServices = content.services.filter((_, i) => i !== index);
    setContent({ ...content, services: newServices });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...content.features];
    newFeatures[index] = value;
    setContent({ ...content, features: newFeatures });
  };

  const addFeature = () => {
    setContent({
      ...content,
      features: [...content.features, '']
    });
  };

  const removeFeature = (index) => {
    const newFeatures = content.features.filter((_, i) => i !== index);
    setContent({ ...content, features: newFeatures });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-stone-800">Gestion du Site</h1>
          <p className="text-stone-600">Gérez le carousel et le contenu de la page d'accueil</p>
        </div>
      </div>

      <Tabs defaultValue="carousel" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="carousel" className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            Carousel d'images
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Contenu textuel
          </TabsTrigger>
        </TabsList>

        {/* ========== CAROUSEL TAB ========== */}
        <TabsContent value="carousel" className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-stone-800">Images du carousel</h2>
              <p className="text-sm text-stone-600">Ces images défilent en arrière-plan de la section hero</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une image
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter une image au carousel</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleImageSubmit} className="space-y-4">
                  <div>
                    <Label>URL de l'image *</Label>
                    <Input
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      required
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-xs text-blue-800">
                      💡 Utilisez des images de haute qualité (min 1920x1080px)
                    </p>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                      Ajouter
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {imagesLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((img, index) => (
                <Card key={img.id}>
                  <CardContent className="p-4">
                    <div className="relative aspect-video rounded-lg overflow-hidden mb-3 bg-stone-100">
                      <img src={img.url} alt={`Carousel ${index + 1}`} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-stone-600">Image {index + 1}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleImageDelete(img.id)}
                        className="text-rose-600 hover:text-rose-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {images.length === 0 && (
                <div className="col-span-full text-center py-12 text-stone-500">
                  <ImageIcon className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                  <p>Aucune image dans le carousel</p>
                  <p className="text-sm">Cliquez sur "Ajouter une image" pour commencer</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ========== CONTENT TAB ========== */}
        <TabsContent value="content" className="space-y-6 mt-6">
          {contentLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
            </div>
          ) : !content ? (
            <div>Erreur de chargement</div>
          ) : (
            <>
              <div className="flex justify-end">
                <Button onClick={handleContentSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </div>

              {/* Section Hero */}
              <Card>
                <CardHeader>
                  <CardTitle>Section Hero</CardTitle>
                  <CardDescription>Contenu principal de la page d'accueil</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="hero-title">Titre principal</Label>
                    <Input
                      id="hero-title"
                      value={content.hero_title}
                      onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hero-subtitle">Sous-titre</Label>
                    <Textarea
                      id="hero-subtitle"
                      value={content.hero_subtitle}
                      onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="hero-btn-primary">Bouton principal</Label>
                      <Input
                        id="hero-btn-primary"
                        value={content.hero_button_primary}
                        onChange={(e) => setContent({ ...content, hero_button_primary: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hero-btn-secondary">Bouton secondaire</Label>
                      <Input
                        id="hero-btn-secondary"
                        value={content.hero_button_secondary}
                        onChange={(e) => setContent({ ...content, hero_button_secondary: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section Services */}
              <Card>
                <CardHeader>
                  <CardTitle>Section Services</CardTitle>
                  <CardDescription>Les prestations affichées sur la page d'accueil</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="services-title">Titre de la section</Label>
                    <Input
                      id="services-title"
                      value={content.services_title}
                      onChange={(e) => setContent({ ...content, services_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="space-y-4">
                    {content.services.map((service, index) => (
                      <div key={index} className="border rounded-lg p-4 space-y-3 bg-stone-50">
                        <div className="flex justify-between items-center">
                          <Label className="text-sm font-semibold">Service {index + 1}</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeService(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div>
                          <Label>Titre</Label>
                          <Input
                            value={service.title}
                            onChange={(e) => updateService(index, 'title', e.target.value)}
                            placeholder="Ex: Mariages"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Input
                            value={service.description}
                            onChange={(e) => updateService(index, 'description', e.target.value)}
                            placeholder="Ex: Couverture complète de votre journée"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button onClick={addService} variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Ajouter un service
                  </Button>
                </CardContent>
              </Card>

              {/* Section Features */}
              <Card>
                <CardHeader>
                  <CardTitle>Section "Ce qui nous distingue"</CardTitle>
                  <CardDescription>Les points forts de vos services</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="features-title">Titre de la section</Label>
                    <Input
                      id="features-title"
                      value={content.features_title}
                      onChange={(e) => setContent({ ...content, features_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>

                  <div className="space-y-3">
                    {content.features.map((feature, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          placeholder="Ex: Film version longue + Teaser inclus"
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFeature(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button onClick={addFeature} variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Ajouter une caractéristique
                  </Button>
                </CardContent>
              </Card>

              {/* Section CTA */}
              <Card>
                <CardHeader>
                  <CardTitle>Section Call-to-Action</CardTitle>
                  <CardDescription>Appel à l'action en bas de page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="cta-title">Titre</Label>
                    <Input
                      id="cta-title"
                      value={content.cta_title}
                      onChange={(e) => setContent({ ...content, cta_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cta-subtitle">Sous-titre</Label>
                    <Textarea
                      id="cta-subtitle"
                      value={content.cta_subtitle}
                      onChange={(e) => setContent({ ...content, cta_subtitle: e.target.value })}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cta-button">Texte du bouton</Label>
                    <Input
                      id="cta-button"
                      value={content.cta_button}
                      onChange={(e) => setContent({ ...content, cta_button: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Footer */}
              <Card>
                <CardHeader>
                  <CardTitle>Footer</CardTitle>
                  <CardDescription>Texte du pied de page</CardDescription>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label htmlFor="footer-text">Texte principal</Label>
                    <Input
                      id="footer-text"
                      value={content.footer_text}
                      onChange={(e) => setContent({ ...content, footer_text: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Bouton de sauvegarde en bas */}
              <div className="flex justify-end">
                <Button onClick={handleContentSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700" size="lg">
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sauvegarde en cours...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-5 w-5" />
                      Enregistrer les modifications
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SiteManager;
